import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
	Client,
	CognitoUser,
	CognitoUserPool,
	EmailUuid,
	Section,
	TokenPack,
	EmailName,
	TokenPackExchangeSource,
	OneTimePasswordExchangeSource,
	CognitoUserPoolsResponse,
} from '@mira-avant/mtlibs';
import { AuthService } from './auth.service';
import { ApiService } from './api.service';

// Response Interfaces
interface SearchUsersResponse {
	success: boolean;
	data: CognitoUser[];
}

interface ListUsersParams {
	pageSize?: number;
	pageNumber?: number;
}

interface TotalUsersResponse {
	success: boolean;
	data: {
		totalUsers: number;
	};
}

interface BulkCreateResponse {
	success: boolean;
	data: Array<{
		username: string;
		temporaryPassword: string;
		cognitoUuid: string;
		userUuid?: string;
		success: boolean;
		error?: string;
	}>;
	error?: string;
}

interface PasswordChangeResponse {
	success: boolean;
	data: {
		token: string;
		refreshToken: string;
	};
	message: string;
}

interface PasswordStatusResponse {
	success: boolean;
	data: {
		needsPasswordChange: boolean;
	};
}

interface BulkOnboardResponse {
	success: boolean;
	data: Array<{
		email: string;
		cognitoUuid: string;
		success: boolean;
		error?: string;
	}>;
}

interface VerifyTokenResponse {
	success: boolean;
	uuid?: string;
	error?: string;
	expired: boolean;
}

interface DisplayNameResponse {
	success: boolean;
	display_name?: string;
	error?: string;
}

export const COGNITO_USER_PAGE_SIZE = 30;

export interface ChangeTemporaryPasswordRequest {
	code: string;
	source: OneTimePasswordExchangeSource;
	newPassword: string;
	subdomain: string;
}

@Injectable({
	providedIn: 'root',
})
export class CognitoService {
	private readonly BASE_PATH = 'cognito';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public pools$: BehaviorSubject<CognitoUserPool[]> = new BehaviorSubject<CognitoUserPool[]>([]);
	public users$: BehaviorSubject<CognitoUser[]> = new BehaviorSubject<CognitoUser[]>([]);
	public totalUsers$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	private emailUserPoolId: string | undefined;
	private uuidUserPoolId: string | undefined;

	constructor(
		private api: ApiService,
		private authService: AuthService
	) {
		this.authService.user$.subscribe(() => {
			this.authService.isAdmin$().then(isAdmin => {
				if (isAdmin) {
					this.initializePools();
				}
			});
		});
	}

	private async initializePools(): Promise<void> {
		const pools = await this.listUserPools();
		this.pools$.next(pools);
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async isCurrentTokenValid(): Promise<boolean> {
		try {
			await this.authService.getExistingOrRefreshedToken();
			return true;
		} catch (error) {
			return false;
		}
	}

	public async exchangeCleverTokenCodeForTokenPack(code: string): Promise<TokenPack> {
		return firstValueFrom(
			this.api.post<TokenPack>(`${this.BASE_PATH}/token-pack/exchange/${TokenPackExchangeSource.CLEVER_SSO}`, {
				code,
			})
		);
	}

	public async listUserPools(): Promise<CognitoUserPool[]> {
		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.get<CognitoUserPoolsResponse>(`${this.BASE_PATH}/list-user-pools`)
			);
			this.emailUserPoolId = response.data.emailPool.id;
			this.uuidUserPoolId = response.data.uuidPool.id;
			return response.data.userPools;
		});
	}

	public isEmailUserPool(poolId: string | undefined): boolean {
		return !!poolId && poolId === this.emailUserPoolId;
	}

	public isUuidUserPool(poolId: string | undefined): boolean {
		return !!poolId && poolId === this.uuidUserPoolId;
	}

	public async bulkOnboardEmailsUsers(
		emails: EmailName[],
		selectedClient: Client,
		selectedSections: Section[],
		selectedGroups: string[],
		activeStartDate: Date | null | undefined,
		activeEndDate: Date | null | undefined,
		active: boolean = true,
		userPoolId: string
	): Promise<BulkOnboardResponse['data']> {
		if (!emails?.length) {
			throw new Error('Valid array of emails is required');
		}
		if (!selectedClient) {
			throw new Error('Selected client is required');
		}
		if (!selectedGroups?.length) {
			throw new Error('Valid array of groups is required');
		}
		if (!userPoolId) {
			throw new Error('User pool ID is required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.post<BulkOnboardResponse>(`${this.BASE_PATH}/bulk-onboard-emails-users`, {
					emails,
					selectedClient,
					selectedSections,
					selectedGroups,
					userPoolId,
					activeStartDate,
					activeEndDate,
					active,
				})
			);

			if (response.success) {
				return response.data;
			}
			throw new Error('Failed to bulk onboard users');
		});
	}

	public async createBulkUuidUsers(
		poolId: string,
		selectedClient: Client,
		selectedSections: Section[],
		selectedGroups: string[],
		activeStartDate: Date | null | undefined,
		activeEndDate: Date | null | undefined,
		active: boolean = true,
		userCount: number
	): Promise<
		Array<{
			username: string;
			temporaryPassword: string;
			cognitoUuid: string;
			userUuid?: string;
			success: boolean;
		}>
	> {
		if (!poolId || !userCount || userCount <= 0 || userCount > 1000) {
			throw new Error('Valid pool ID and user count (1-1000) are required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.post<BulkCreateResponse>(`${this.BASE_PATH}/bulk-create-uuid-users`, {
					userPoolId: poolId,
					selectedClient,
					selectedSections,
					selectedGroups,
					activeStartDate,
					activeEndDate,
					active,
					userCount: userCount,
				})
			);

			if (response.success) {
				return response.data;
			}
			throw new Error('Failed to create bulk users');
		});
	}

	public downloadUsersAsCsv(
		users: Array<{
			username: string;
			temporaryPassword: string;
			cognitoUuid: string;
			userUuid?: string;
			success: boolean;
		}>,
		filename: string = 'bulk_users.csv'
	): void {
		const csvContent = [
			'Username,TemporaryPassword',
			...users.map(user => `${user.username},${user.temporaryPassword}`),
		].join('\n');

		const blob = new Blob([csvContent], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		link.click();
		window.URL.revokeObjectURL(url);
	}

	public async changeTemporaryPassword(request: ChangeTemporaryPasswordRequest): Promise<TokenPack> {
		if (!request.newPassword || !request.subdomain) {
			throw new Error('New password and subdomain are required');
		}

		if (!request.code || !request.source) {
			throw new Error('One-time password code and source are required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.post<TokenPack>(`${this.BASE_PATH}/change-temporary-password`, {
					...request,
				})
			);

			if (!response.success) {
				throw new Error(response.error || 'Failed to change password');
			}

			return await this.authService.loginFromTokenPack(response);
		});
	}

	public async isOneTimePasswordCodeValid(code: string, source: OneTimePasswordExchangeSource): Promise<boolean> {
		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.post<{ valid: boolean }>(`${this.BASE_PATH}/validate-one-time-password-code`, {
					code,
					source,
				})
			);

			return response.valid;
		});
	}

	public async getTotalUsers(poolId: string): Promise<number> {
		if (!poolId) {
			console.warn('No poolId provided');
			return 0;
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom<TotalUsersResponse>(
				this.api.get<TotalUsersResponse>(`${this.BASE_PATH}/total-users/${poolId}`)
			);

			if (response.success) {
				this.totalUsers$.next(response.data.totalUsers);
				return response.data.totalUsers;
			}
			throw new Error('Failed to get total users count');
		});
	}

	public async getUserByUsername(poolId: string, username: string): Promise<CognitoUser> {
		if (!poolId || !username) {
			throw new Error('Pool ID and username are required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.get<{ success: boolean; data: CognitoUser }>(
					`${this.BASE_PATH}/user-by-username/${poolId}/${encodeURIComponent(username)}`
				)
			);

			if (response.success) {
				return response.data;
			}
			throw new Error('Failed to get user');
		});
	}

	public async getUserByCognitoUuid(poolId: string, sub: string): Promise<CognitoUser> {
		if (!poolId || !sub) {
			throw new Error('Pool ID and sub (UUID) are required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.get<{ success: boolean; data: CognitoUser }>(
					`${this.BASE_PATH}/user-by-cognito-uuid/${poolId}/${sub}`
				)
			);

			if (response.success) {
				return response.data;
			}
			throw new Error('Failed to get user');
		});
	}

	public async getUserPoolByUserUuid(userUuid: string): Promise<string> {
		if (!userUuid) {
			throw new Error('User UUID is required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.get<{ userPoolId: string }>(`${this.BASE_PATH}/user_pool_id/${userUuid}`)
			);

			return response.userPoolId;
		});
	}

	public async listUsers(
		poolId: string,
		params: ListUsersParams = { pageSize: COGNITO_USER_PAGE_SIZE, pageNumber: 1 }
	): Promise<CognitoUser[]> {
		if (!poolId) {
			console.warn('No poolId provided');
			return [];
		}

		return this.withLoading(async () => {
			const httpParams = {
				pageSize: params.pageSize?.toString() || COGNITO_USER_PAGE_SIZE.toString(),
				pageNumber: params.pageNumber?.toString() || '1',
			};

			const response = await firstValueFrom(
				this.api.get<{ success: boolean; data: CognitoUser[] }>(`${this.BASE_PATH}/list-users/${poolId}`, {
					params: httpParams,
				})
			);

			if (response.success) {
				this.users$.next(response.data);
				return response.data;
			}

			throw new Error('Failed to list users');
		});
	}

	public async searchUsers(poolId: string, searchTerm: string): Promise<CognitoUser[]> {
		if (!poolId || !searchTerm) {
			console.warn('Pool ID and search term are required');
			return [];
		}

		return this.withLoading(async () => {
			const params = { searchTerm };

			const response = await firstValueFrom(
				this.api.get<SearchUsersResponse>(`${this.BASE_PATH}/search/${poolId}`, {
					params,
				})
			);

			if (response.success) {
				this.users$.next(response.data);
				return response.data;
			}

			throw new Error('Failed to search users');
		});
	}

	public async listUserGroups(poolId: string): Promise<string[]> {
		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.get<{ success: boolean; data: { name: string }[] }>(
					`${this.BASE_PATH}/list-user-groups/${poolId}`
				)
			);
			if (response.success) {
				return response.data.map(group => group.name);
			}
			throw new Error('Failed to list user groups');
		});
	}

	public async updateUserGroups$(userPoolId: string, cognitoUuid: string, newGroups: string[]): Promise<CognitoUser> {
		if (!userPoolId || !cognitoUuid || !newGroups) {
			throw new Error('User pool ID, Cognito UUID, and new groups are required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.post<{ success: boolean; data: CognitoUser }>(`${this.BASE_PATH}/update-groups`, {
					user_pool_id: userPoolId,
					cognito_uuid: cognitoUuid,
					new_groups: newGroups,
				})
			);

			if (response.success) {
				const currentUsers = this.users$.getValue();
				const updatedUsers = currentUsers.map(user =>
					user.uuid === cognitoUuid ? { ...user, groups: response.data.groups } : user
				);
				this.users$.next(updatedUsers);

				return response.data;
			}
			throw new Error('Failed to update user groups');
		});
	}

	public async getUuidsByEmails(emails: string[]): Promise<EmailUuid[]> {
		if (!emails?.length) {
			throw new Error('Valid array of emails is required');
		}

		return this.withLoading(async () => {
			const response = await firstValueFrom(
				this.api.post<{ success: boolean; data: EmailUuid[] }>(`${this.BASE_PATH}/get-uuids-by-emails`, {
					emails,
				})
			);

			if (response.success) {
				return response.data;
			}
			throw new Error('Failed to get UUIDs for emails');
		});
	}

	/**
	 * Get displayname directly from cognito.
	 *
	 * @public
	 * @async
	 * @param {{
	 *         user_uuid?: string;
	 *         cognito_uuid?: string;
	 *         userPoolId?: string;
	 *     }} params
	 * @returns {Promise<string>}
	 */
	public async getDisplayName(params: {
		user_uuid?: string;
		cognito_uuid?: string;
		userPoolId?: string;
	}): Promise<string> {
		return await this.withLoading(async () => {
			try {
				const queryParams: Record<string, string> = {};

				if (params.user_uuid) {
					queryParams['user_uuid'] = params.user_uuid;
				} else if (params.cognito_uuid) {
					queryParams['cognito_uuid'] = params.cognito_uuid;
				}

				if (params.userPoolId) {
					queryParams['userPoolId'] = params.userPoolId;
				}

				const response = await firstValueFrom(
					this.api.get<DisplayNameResponse>(`${this.BASE_PATH}/display_name`, {
						params: queryParams,
					})
				);

				if (!response.success) {
					console.error(response.error || 'Failed to fetch display name');
					return params.user_uuid || params.cognito_uuid || 'N/A';
				}

				return response.display_name || '';
			} catch (error) {
				console.error(error);
				return params.user_uuid || params.cognito_uuid || 'N/A';
			}
		});
	}
}
